import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { eachDayOfInterval, getDay, isBefore } from 'date-fns'
import { sortBy, difference, intersection, uniq } from 'lodash'
import { Trans } from 'react-i18next'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { DateTimePicker, Button, Checkbox, FormRow, Form } from 'components/Forms'
import { Collapsable, Loading, AutomaticButtonGroup } from 'components/UI'
import { Format, Parse } from 'services/Dates'
import { closeModal } from 'store/actions/modals'
import { refreshCounter } from 'store/actions/refresh'
import { Alert, Card } from 'ui'

import { t } from 'translations'

const ScheduleTemplateApplyModal = ({ _id }) => {
  const dispatch = useDispatch()
  const [template, setTemplate] = useState()
  const [errorLoading, setErrorLoading] = useState(false)

  useEffect(() => {
    const fetchScheduleTemplate = async () => {
      const { result, success } = await Api(`schedule/template`, { params: { _id, useNewReturn: true } })
      if (!success || !result.length) return setErrorLoading(true)
      return setTemplate(result[0])
    }

    fetchScheduleTemplate()
  }, [_id])

  const applyTemplate = async ({ newDates, modifiedDates, deleteShifts, dontUpdateUsers, dontUpdateTags, dontUpdateComments, dontUpdateTime }, actions) => {
    try {
      const { success } = await Api.post('schedule/template/apply', {
        newDates,
        modifiedDates,
        deleteMode: deleteShifts,
        _id,
        update: { employee: !dontUpdateUsers, tags: !dontUpdateTags, comments: !dontUpdateComments, time: !dontUpdateTime }
      })
      if (success) {
        dispatch(closeModal())
        dispatch(refreshCounter('scheduleTemplates'))
      }
      // Reason for try catch plus throwing error is to catch timeout when applying a template over too many days
      else throw new Error('Error applying template')
    } catch (error) {
      // If there is an error while submitting the template
      actions.setStatus({ type: 'danger', msg: 'manager.scheduleTemplates.applyForm.ERROR_APPLYING_TEMPLATE' })
      actions.setSubmitting(false)
    }
  }

  if (errorLoading)
    return (
      <BaseModal hideButtons title={`manager.scheduleTemplates.SCHEDULE_TEMPLATE_APPLY_MODAL`}>
        {t('manager.scheduleTemplates.TEMPLATE_DELETED')}
        <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
      </BaseModal>
    )
  if (!template) return <Loading />

  const isMulti = Array.isArray(template.multiDayTemplate) && template.multiDayTemplate.length

  return (
    <BaseModal shouldCloseOnOverlayClick={false} hideButtons title={`manager.scheduleTemplates.SCHEDULE_TEMPLATE_APPLY_MODAL`}>
      <Formik
        enableReinitialize={true}
        initialValues={{
          datesToApply: isMulti ? [] : Object.keys(template.dates || {}),
          rangeToApply: [],
          newDates: [],
          modifiedDates: [],
          deleteShifts: false,
          applyInFuture: true,
          dontUpdateUsers: false,
          dontUpdateTags: false,
          dontUpdateComments: false,
          dontUpdateTime: false,
          multiSelectRange: [],
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }}
        onSubmit={applyTemplate}
      >
        {({ isSubmitting, setFieldValue, values, status, setStatus }) => {
          const appliedDates = Object.keys(template.dates || {})
          const removeAllDates = () => setFieldValue('datesToApply', [])
          const setAppliedDates = () => setFieldValue('datesToApply', appliedDates)

          const numberOfdatesSelectedInPast = values.datesToApply.reduce((acc, date) => (isBefore(Parse(date), new Date()) ? acc + 1 : acc), 0)

          return (
            <Form>
              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>

              {isMulti ? (
                <DateTimePicker module="manager.scheduleTemplates.applyForm" name="datesToApply" type="range" />
              ) : (
                <DateTimePicker
                  module="manager.scheduleTemplates.applyForm"
                  name="datesToApply"
                  type="dateMultiple"
                  onOpen={(selectedDates, dateStr, instance) => {
                    instance.jumpToDate(new Date())
                  }}
                  inputAppend={
                    <>
                      <Button disabled={!appliedDates.length} onClick={() => setAppliedDates()} className="btn btn-outline-secondary">
                        <i className="fas fa-sync-alt" />
                      </Button>
                      <Button disabled={!values.datesToApply.length} onClick={() => removeAllDates()} className="btn btn-outline-secondary">
                        <i className="fas fa-trash" />
                      </Button>
                    </>
                  }
                />
              )}

              <Card>
                <Card.Body>
                  <FormRow>
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="deleteShifts" />
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="applyInFuture" />
                  </FormRow>
                  <FormRow>
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="dontUpdateUsers" />
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="dontUpdateTags" />
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="dontUpdateComments" />
                    <Checkbox module="manager.scheduleTemplates.applyForm" name="dontUpdateTime" />
                  </FormRow>
                </Card.Body>
              </Card>

              {/* Date multi-select */}
              {isMulti ? null : (
                <Collapsable type="multiSelect" title="manager.scheduleTemplates.applyForm.MULTI_SELECT">
                  <DateMultiSelect setFieldValue={setFieldValue} template={template} values={values} removeAllDates={removeAllDates} />
                </Collapsable>
              )}

              <TuneDates appliedDates={appliedDates} setFieldValue={setFieldValue} values={values} isMulti={isMulti} />

              <Alert variant="warning">
                {numberOfdatesSelectedInPast > 0 && values.applyInFuture ? (
                  <div>
                    <Trans t={t} count={numberOfdatesSelectedInPast} i18nKey={`manager.scheduleTemplates.applyForm.NUMBER_OF_DATES_SELECTED_PAST`}>
                      <b></b>
                    </Trans>
                  </div>
                ) : null}
                <div>
                  <Trans
                    t={t}
                    count={values.modifiedDates.length}
                    i18nKey={`manager.scheduleTemplates.applyForm.${values.deleteShifts ? 'DELETED_DATES' : 'MODIFIED_DATES'}`}
                  >
                    <b></b>
                  </Trans>
                </div>
                <div>
                  {values.deleteShifts ? null : (
                    <Trans t={t} count={values.newDates.length} i18nKey="manager.scheduleTemplates.applyForm.NEW_DATES">
                      <b></b>
                    </Trans>
                  )}
                </div>
              </Alert>

              <div className="mt-2">
                <Button
                  className="btn btn-primary mr-2"
                  type="submit"
                  isSubmitting={isSubmitting}
                  text="SUBMIT"
                  disabled={!values.newDates.length && !values.modifiedDates.length}
                />
                <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

const TuneDates = ({ appliedDates, setFieldValue, isMulti, values }) => {
  const { applyInFuture, deleteShifts } = values
  // Loads dates this template should be applied to, takes a range when a multi day template
  const getDatesToApply = () => {
    if (isMulti) {
      if (values.datesToApply.length !== 2) return []
      const [start, end] = values.datesToApply
      return eachDayOfInterval({ start: Parse(start), end: Parse(end) }).map(date => Format(date, { outFormat: 'date' }))
    } else return values.datesToApply
  }

  // If applyInFuture is not active, apply all dates
  const datesToApply = getDatesToApply().filter(date => (applyInFuture ? !isBefore(Parse(date, { inFormat: 'date' }), new Date()) : true))

  // New dates are dates that have no shifts from this template on them
  const newDates = sortBy(difference(datesToApply, appliedDates))
  const newDatesString = newDates.join(',')

  // Modified dates are dates that already contain shifts from this template
  const modifiedDates = sortBy(intersection(appliedDates, datesToApply))
  const modifiedDatesString = modifiedDates.join(',')

  // .join(',') is used here because otherwise the equality check does not work on the useEffect and
  // it reloads infinitely. Converting to string means it always points to the same string primitive.
  useEffect(() => {
    setFieldValue('newDates', newDatesString.length > 0 ? newDatesString.split(',') : [])
    setFieldValue('modifiedDates', modifiedDatesString.length > 0 ? modifiedDatesString.split(',') : [])
  }, [newDatesString, modifiedDatesString, setFieldValue])

  return (
    <Collapsable type="fineTune" title="manager.scheduleTemplates.applyForm.FINE_TUNE">
      <DateTimePicker
        options={{ enable: modifiedDates }}
        disabled={!modifiedDates.length}
        module="manager.scheduleTemplates.applyForm"
        name="modifiedDates"
        type="dateMultiple"
        translation={deleteShifts ? 'deletedDates' : 'modifiedDates'}
      />
      {deleteShifts ? null : (
        <DateTimePicker
          options={{ enable: newDates }}
          disabled={!newDates.length}
          module="manager.scheduleTemplates.applyForm"
          name="newDates"
          type="dateMultiple"
        />
      )}
    </Collapsable>
  )
}

const DateMultiSelect = ({ setFieldValue, values, removeAllDates }) => {
  const setDates = () => {
    const { datesToApply, multiSelectRange, monday, tuesday, wednesday, thursday, friday, saturday, sunday } = values
    const selectedDays = [sunday, monday, tuesday, wednesday, thursday, friday, saturday]
    // uses boolean array of selectedDays to generate an array of all days between start and end of
    // range and filter only activated days of week, then parses to an array of dates in correct format
    const dates = eachDayOfInterval({ start: Parse(multiSelectRange[0]), end: Parse(multiSelectRange[1]) })
      .filter(value => selectedDays[getDay(value)])
      .map(date => Format(date, { outFormat: 'date' }))
    setFieldValue('datesToApply', uniq([...dates, ...datesToApply]))
  }

  return (
    <>
      <DateTimePicker module="manager.scheduleTemplates.applyForm" name="multiSelectRange" type="range" />
      <div className="mb-2">
        <AutomaticButtonGroup>
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="monday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="tuesday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="wednesday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="thursday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="friday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="saturday" />
          <Checkbox buttonCheckbox module="manager.scheduleTemplates.applyForm" name="sunday" />
        </AutomaticButtonGroup>
        <small className="form-text text-muted m">{t(`manager.scheduleTemplates.applyForm.DAY_OF_WEEK_DESCRIPTION`)}</small>
      </div>
      <Button
        disabled={!values.multiSelectRange.length}
        className="btn btn-primary mr-2"
        text="manager.scheduleTemplates.applyForm.LOAD_DATES"
        onClick={setDates}
      />
      <Button
        disabled={!values.datesToApply.length}
        className="btn btn-primary mr-2"
        text="manager.scheduleTemplates.applyForm.REMOVE_ALL_DATES"
        onClick={() => removeAllDates()}
      />
    </>
  )
}

export default ScheduleTemplateApplyModal
